import { useSuspenseQuery } from "@tanstack/react-query";
import { ArrowRight, CreditCard } from "lucide-react";
import { Suspense } from "react";

import { orpc } from "@/client";
import { ErrorBoundary } from "@/components/error-boundary";
import { Button } from "@/components/ui/button";
import { EmptyState } from "@/components/ui/empty-state";
import { FinanceiroLabels } from "@/lib/labels/financeiro";
import type { CardSettlementSummary } from "@/types/financeiro";

import { FinanceListSkeleton } from "./area-skeleton";
import { StatePill } from "./primitives";
import { FinanceTimeline, FinanceTimelineCard } from "./timeline";
import type { FinanceModal } from "./types";

const SETTLEMENT_STATUS_STATE = {
	pending: { label: "Aguardando pagamento", tone: "muted" as const },
	rejected: { label: "Rejeitado", tone: "coral" as const },
	suspended: { label: "Suspenso", tone: "coral" as const },
};

const SETTLEMENT_ISSUE_STATE: Record<
	NonNullable<CardSettlementSummary["issue_kind"]>,
	{ label: string; tone: "amber" | "coral" }
> = {
	card_settlement_components_missing: { label: "Sem detalhamento", tone: "amber" },
	card_settlement_divergent: { label: "Valores divergentes", tone: "coral" },
	card_settlement_sales_incomplete: { label: "Vendas incompletas", tone: "coral" },
	card_settlement_sales_chargeback: { label: "Chargeback", tone: "coral" },
	card_settlement_sales_reversed: { label: "Venda cancelada", tone: "coral" },
	card_settlement_sales_unmatched: { label: "Faltam pacientes", tone: "amber" },
	card_settlement_unmatched: { label: "Falta entrada bancária", tone: "amber" },
	card_settlement_evidence_lost: { label: "Falta entrada bancária", tone: "amber" },
};

export function ReconciliationSettlements({
	month,
	onModal,
}: {
	month: string;
	onModal: (modal: FinanceModal) => void;
}) {
	return (
		<ErrorBoundary key={month}>
			<Suspense fallback={<FinanceListSkeleton />}>
				<SettlementList month={month} onModal={onModal} />
			</Suspense>
		</ErrorBoundary>
	);
}

function SettlementList({
	month,
	onModal,
}: {
	month: string;
	onModal: (modal: FinanceModal) => void;
}) {
	const { data } = useSuspenseQuery(
		orpc.financeiro.reconciliation.settlements.queryOptions({ input: { month } }),
	);
	const reconciled = data.data.filter(
		(settlement) => settlement.status === "paid" && settlement.issue_kind === null,
	).length;
	const reconciledLabel = data.data.length === 1 ? "conciliado" : "conciliados";

	return (
		<section className="mt-5">
			<div className="flex items-center justify-between gap-4">
				<div>
					<h2 className="type-section-title text-foreground">Repasses da Rede</h2>
					<p className="text-xs text-muted-foreground">
						{reconciled} de {data.data.length} {reconciledLabel}
					</p>
				</div>
				<CreditCard className="size-5 text-primary" />
			</div>
			{data.data.length === 0 && (
				<div className="surface-panel mt-4">
					<EmptyState
						icon={CreditCard}
						title="Nenhum repasse neste mês"
						description="Os pagamentos enviados pela Rede aparecem aqui."
					/>
				</div>
			)}
			{data.data.length > 0 && (
				<FinanceTimeline
					items={data.data}
					dateFor={(settlement) => settlement.settled_on}
					itemKey={(settlement) => settlement.external_id}
					renderItem={(settlement) => {
						const state = settlementState(settlement);
						const identifiedCount = settlement.sale_count - settlement.unidentified_sale_count;
						const identifiedLabel =
							settlement.sale_count === 1
								? `${identifiedCount} de 1 venda identificada`
								: `${identifiedCount} de ${settlement.sale_count} vendas identificadas`;

						return (
							<FinanceTimelineCard
								icon={CreditCard}
								title={identifiedLabel}
								detail={`${FinanceiroLabels.money(settlement.gross_cents)} bruto neste repasse`}
								amount={FinanceiroLabels.money(settlement.net_cents)}
								status={<StatePill label={state.label} tone={state.tone} />}
								actions={
									<Button
										variant="ghost"
										size="sm"
										onClick={() => onModal({ kind: "settlement", id: settlement.external_id })}
									>
										Abrir
										<ArrowRight />
									</Button>
								}
							/>
						);
					}}
				/>
			)}
		</section>
	);
}

function settlementState(settlement: CardSettlementSummary) {
	if (settlement.status !== "paid") {
		return SETTLEMENT_STATUS_STATE[settlement.status];
	}

	if (settlement.issue_kind !== null) {
		return SETTLEMENT_ISSUE_STATE[settlement.issue_kind];
	}

	return { label: "Conciliado", tone: "green" as const };
}
